/**
 * Groups API - Members
 *
 * List, add, and manage group members.
 */

import { z } from 'zod'
import { GroupMemberRoleAPISchema } from './common'

// ============================================================================
// GET /v1/groups/:groupId/members
// ============================================================================

export const GetGroupMembersRequestSchema = z.object({
  group_id: z.string(),
  limit: z.number().min(1).max(1000).optional(),
  offset: z.number().min(0).optional()
})

export type GetGroupMembersRequest = z.infer<typeof GetGroupMembersRequestSchema>

/**
 * One roster row on `GET /v1/groups/:groupId/members`.
 *
 * TWO PERSON IDENTIFIERS, DELIBERATELY DISTINCT — do not conflate them:
 *
 * - `schoolUserId` carries the Blackbaud `UsersOnSchools.platformId` and is
 *   PII-capped to admin-like callers; everyone else receives `''`. It is a
 *   disclosure field, NOT a join key: for a plain member every row's value is
 *   `''`, so keying a roster map on it collapses the whole roster to one entry.
 * - `schoolUserPublicId` carries `UsersOnSchools.publicId` for EVERY caller and
 *   is the identifier clients join a member row against. Nullable because the
 *   column is; a null is a data anomaly the server logs, and a client that
 *   cannot resolve a row must render no management affordance for it rather
 *   than falling back to another id.
 *
 * `role` is the member's stored `SchoolUsersOnGroups.permissionLevel`. It was
 * declared required here long before the server emitted it — the serializer read
 * a `role` column that does not exist on the membership model and the field was
 * dropped from every response. Nothing runtime-parses this schema on the server
 * hot path by design; drift is caught by the response-schema assertion in
 * `apps/backend/tests/gray/groups/group-member-list-*.gray.test.ts`.
 */
export const GroupMemberAPISchema = z.object({
  id: z.string(),
  publicId: z.string(),
  name: z.string(),
  role: GroupMemberRoleAPISchema,
  schoolUserId: z.string(),
  schoolUserPublicId: z.string().nullable(),
  photoUrl: z.string().nullable().optional(),
  description: z.string().optional(),
  canView: z.boolean(),
  canPost: z.boolean(),
  canComment: z.boolean(),
  canInvite: z.boolean(),
  canReact: z.boolean(),
  joinedAt: z.string(),
  removedAt: z.string().nullable(),
  school: z.object({
    id: z.string().nullable(),
    name: z.string().nullable()
  })
})

export type GroupMemberAPI = z.infer<typeof GroupMemberAPISchema>

/**
 * The group the roster belongs to, stated by the server on every member read.
 *
 * `groupType` is here because it is otherwise UNREACHABLE for a chat client. The
 * session payload's group list (`getAllGroups` in `get-school-user-by-id.ts`)
 * passes no `includeDirectMessages`, so `DIRECT_MESSAGE`, `GROUP_MESSAGE` and
 * `CONTACT_INQUIRY` are filtered out of it — a client deriving an ad-hoc chat's
 * type from that list gets `undefined` for EVERY ad-hoc chat, and every
 * type-gated decision downstream then fails closed on a group whose type the
 * server knew all along.
 *
 * That is not hypothetical: `useGroupInfo`'s `chatSurfaceKind` read the session
 * list, resolved `unknown` for every group chat, and stripped the management
 * rows from all of them. The rule this field encodes is the plan's own — the
 * server states the answer, the client renders it — so the type travels with the
 * roster it describes rather than being re-derived from a payload that excludes
 * it.
 */
export const GroupMemberListGroupSchema = z.object({
  /** The group's `UserGroup.publicId` — the same id the request was keyed by. */
  id: z.string(),
  /** Stored `UserGroup.groupType`. `z.string()`, matching every other group contract. */
  groupType: z.string()
})

export type GroupMemberListGroup = z.infer<typeof GroupMemberListGroupSchema>

export const GetGroupMembersResponseSchema = z.object({
  data: z.array(GroupMemberAPISchema),
  group: GroupMemberListGroupSchema,
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    message: z.string().optional()
  }),
  message: z.string()
})

export type GetGroupMembersResponse = z.infer<typeof GetGroupMembersResponseSchema>

// ============================================================================
// POST /v1/groups/:groupId/members
// ============================================================================

export const AddGroupMemberRequestSchema = z.object({
  groupPublicId: z.string(),
  userId: z.string().optional(),
  members: z.array(z.string()).optional(),
  role: z.string().optional()
})

export type AddGroupMemberRequest = z.infer<typeof AddGroupMemberRequestSchema>

const MemberAddResultSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  profilePictureUrl: z.string().nullable(),
  role: z.string(),
  joinedAt: z.string()
})

export const AddGroupMemberResponseSchema = z.object({
  message: z.string(),
  data: z.union([
    MemberAddResultSchema.nullable(),
    z.object({
      added: z.array(MemberAddResultSchema),
      alreadyMembers: z.array(MemberAddResultSchema),
      errors: z.array(
        z.object({
          userId: z.string(),
          error: z.string()
        })
      )
    })
  ])
})

export type AddGroupMemberResponse = z.infer<typeof AddGroupMemberResponseSchema>

// ============================================================================
// PATCH /v1/groups/:groupId/members/permissions
// ============================================================================

// LATENT-04A-FIX: shape was previously `{ memberId, permissions: Record<string, boolean> }`
// but backend reality at `apps/backend/src/api/groups/v1/use-cases/(members)/member-permissions-update/schema.ts:21-25`
// is an array of `{ id, permissionLevel?, canView?, canReact?, canComment?, canPost?, canModerate?, canInvite? }`.
// Dormant in production — no consumer reads the broken schema; chat code at
// `services/chat/group-api-service.ts:440` already builds the array shape via
// direct `api.*` call. Surfaced during Tier 2 Chat pilot PR C.5a pre-flight audit.
// See `docs/plans/mobile-deep-modules/known-issues.md` for the full entry.

export const MemberPermissionLevelSchema = z.enum(['ADMIN', 'MODERATOR', 'MEMBER', 'GUEST'])

export type MemberPermissionLevel = z.infer<typeof MemberPermissionLevelSchema>

export const MemberPermissionUpdateItemSchema = z.object({
  id: z.string(),
  permissionLevel: MemberPermissionLevelSchema.optional(),
  canView: z.boolean().optional(),
  canReact: z.boolean().optional(),
  canComment: z.boolean().optional(),
  canPost: z.boolean().optional(),
  canModerate: z.boolean().optional(),
  canInvite: z.boolean().optional()
})

export type MemberPermissionUpdateItem = z.infer<typeof MemberPermissionUpdateItemSchema>

export const UpdateMemberPermissionsRequestSchema = z.array(MemberPermissionUpdateItemSchema).min(1)

export type UpdateMemberPermissionsRequest = z.infer<typeof UpdateMemberPermissionsRequestSchema>

export const UpdateMemberPermissionsResponseSchema = z.object({
  message: z.string(),
  data: z.array(
    z.object({
      id: z.string(),
      permissionLevel: z.enum(['OWNER', 'ADMIN', 'MODERATOR', 'MEMBER', 'GUEST']),
      canView: z.boolean(),
      canReact: z.boolean(),
      canComment: z.boolean(),
      canPost: z.boolean(),
      canModerate: z.boolean(),
      canInvite: z.boolean()
    })
  )
})

export type UpdateMemberPermissionsResponse = z.infer<typeof UpdateMemberPermissionsResponseSchema>

// ============================================================================
// POST /v1/groups/:groupId/members/handoff
// ============================================================================

/**
 * Permanent-Admin handoff on an ad-hoc chat group
 * (plan 2026-08-14-002, U8 / KTD4 / KTD14 / KTD18).
 *
 * "Permanent Admin" is the stored `OWNER` membership row and nothing else
 * (KTD14). One request moves that row to a successor and removes the departing
 * holder, or does neither — the two halves have no compensating action if split
 * across calls.
 *
 * Scoped to `groupType === 'GROUP_MESSAGE'` (KTD13). Direct messages carry
 * stored `OWNER` rows too and are refused here so their self-removal keeps
 * routing through the archive path that owns it.
 *
 * - `successorUserId` — a `UsersOnSchools` publicId (a numeric `platformId` is
 *   also accepted, school-scoped). Must be an ACTIVE member of the group, must
 *   not be the departing holder, and must not be a `GUEST` row.
 * - `departingUserId` — omit for the ordinary case, where the caller is the
 *   departing permanent Admin. Naming someone else requires school-admin or
 *   super-admin standing (KTD18) and exists so R16's refusal always has a
 *   remedy inside the product.
 *
 * `UserGroup.createdById` is NEVER written by this endpoint — it is immutable
 * attribution feeding the chat-creation rate cap (KTD17). The creator marker
 * derives from the stored `OWNER` row instead.
 */
export const GroupPermanentAdminHandoffRequestSchema = z.object({
  successorUserId: z.string().min(1),
  departingUserId: z.string().min(1).optional()
})

export type GroupPermanentAdminHandoffRequest = z.infer<typeof GroupPermanentAdminHandoffRequestSchema>

export const GroupPermanentAdminHandoffResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    groupId: z.string(),
    /** `UsersOnSchools.publicId` of the person who no longer holds the row, and is no longer a member. */
    departingUserId: z.string(),
    /** `UsersOnSchools.publicId` of the new permanent Admin. */
    successorUserId: z.string(),
    /** Always `OWNER` — stated so a client never has to re-derive it. */
    successorRole: z.literal('OWNER')
  })
})

export type GroupPermanentAdminHandoffResponse = z.infer<typeof GroupPermanentAdminHandoffResponseSchema>

// ============================================================================
// POST /v1/groups/:groupId/members/remove
// ============================================================================

/**
 * Batch member removal — the route that enforces `canRemoveByHierarchy`
 * (plan 2026-07-14-003, U8).
 *
 * NOT to be confused with `DELETE /v1/groups/:groupId/members/:userId`, which
 * removes a single member and performs **no requester authorization** on its
 * admin-remove branch (SCH-707). New removal consumers belong on THIS route.
 *
 * Each id is a group-membership publicId (the backend also accepts a school-user
 * publicId as a fallback lookup). The response echoes only the memberships that
 * were actually transitioned to REMOVED.
 */
export const RemoveGroupMembersRequestSchema = z.array(z.string()).min(1)

export type RemoveGroupMembersRequest = z.infer<typeof RemoveGroupMembersRequestSchema>

export const RemoveGroupMembersResponseSchema = z.object({
  message: z.string(),
  data: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      removedAt: z.string()
    })
  )
})

export type RemoveGroupMembersResponse = z.infer<typeof RemoveGroupMembersResponseSchema>
